// src/features/simulators/shared/types/journey.types.ts

/**
 * Event Codes according to journey documentation
 * Critical distinction: PC_* for remote, CI_* for on-premises
 */
export enum JourneyEventCode {
  // Pre-check-in events (REMOTE - before arriving at hotel)
  PC_START = 'PC_START',           // Pre-check-in initiated remotely
  PC_COMP = 'PC_COMP',             // Pre-check-in completed

  // Check-in events (ON-PREMISES - at the hotel)
  CI_START = 'CI_START',           // Check-in started at hotel
  CI_COMP = 'CI_COMP',             // Check-in completed at hotel

  // Section navigation events
  SECTION_ENTER = 'SECTION_ENTER',
  SECTION_EXIT = 'SECTION_EXIT',
  SECTION_UPDATE = 'SECTION_UPDATE',

  // Payment events
  PM_START = 'PM_START',           // Payment process started
  PM_SUCCESS = 'PM_SUCCESS',       // Payment successful
  PM_ERROR = 'PM_ERROR',           // Payment failed

  // Key dispensing events
  KD_SUCCESS = 'KD_SUCCESS',       // Key dispensed successfully
  KD_ERROR = 'KD_ERROR',           // Key dispensing failed

  // Error and status events
  AB = 'AB',                       // Abandonment
  ERR = 'ERR',                     // Generic error
  HB = 'HB',                       // Heartbeat
}

/**
 * Section codes for journey steps
 */
export enum SectionCode {
  S = 'S',  // Search/Selection
  R = 'R',  // Room Selection (Walking guests only)
  I = 'I',  // Identification
  F = 'F',  // Signature
  U = 'U',  // Upselling
  P = 'P',  // Payment
  K = 'K',  // Key Delivery
}

/**
 * QR Code types for correlation
 */
export enum QRCodeType {
  RESERVATION_NUMBER = 'ReservationNumber',  // QR contains reservation number
  FULL_INFO = 'FullInfo',                   // QR contains room + metadata
  UNIQUE_ID = 'UniqueId',                   // QR contains unique correlation ID
}

/**
 * Guest types
 */
export enum GuestType {
  RESERVATION = 'Reservation',  // Guest with existing reservation
  WALKING = 'Walking',          // Walk-in guest without reservation
}

/**
 * Key delivery methods
 */
export enum KeyDeliveryMethod {
  // Physical keys
  MAGNETIC_CARD = 'magnetic_card',        // Magnetic stripe card
  RFID_CARD = 'rfid_card',                // RFID card
  CONVENTIONAL_KEY = 'conventional_key',   // Conventional metal key
  
  // Digital keys
  DIGITAL_PIN = 'digital_pin',            // PIN code
  DIGITAL_MOBILE = 'digital_mobile',      // Mobile phone key
  
  // Other methods
  QR_DISPENSER = 'qr_dispenser',          // QR code for dispenser pickup
  MANUAL = 'manual',                      // Pickup at reception
}

/**
 * Device types for simulation
 */
export enum DeviceType {
  ONLINE_CHECKIN = 'web-checkin',
  KIOSK = 'kiosk',
  MOBILE_KIOSK = 'mobile-kiosk',
  MOBILE_DESK = 'mobile-desk',
  DISPENSER = 'dispenser',
}

/**
 * Base metadata that all events should include
 */
export interface BaseEventMetadata {
  timestamp?: string;
  deviceVersion?: string;
  [key: string]: any; // Allow additional fields
}

/**
 * Metadata for check-in start events
 */
export interface CheckInStartMetadata extends BaseEventMetadata {
  isWalking?: string;            // "true" for walking guests
  hasPreCheckIn?: string;        // "true" if continuing pre-check-in
  roomNumber?: string;           // Room if already assigned
  checkInType?: GuestType;
}

/**
 * Metadata for check-in complete events
 */
export interface CheckInCompleteMetadata extends BaseEventMetadata {
  roomNumber: string;            // REQUIRED for correlation
  isWalking?: string;            // "true" for walking guests
  keyDispensed?: string;         // "true" if key was dispensed
  qrGenerated?: string;          // "true" if QR was generated
  qrType?: QRCodeType;          // Type of QR generated
  qrUniqueId?: string;          // Unique ID in QR for correlation
  keyType?: string;             // physical/digital
  keyDeliveryMethod?: string;   // How key will be delivered
  totalDuration?: string;       // Total process duration in seconds
  pmsRegistered?: string;       // "true" when registered in PMS
}

/**
 * Metadata for key dispensing events
 */
export interface KeyDispenseMetadata extends BaseEventMetadata {
  roomNumber: string;            // REQUIRED for walking guest correlation
  qrCodeType?: QRCodeType;      // Type of QR scanned
  qrUniqueId?: string;          // Unique ID from QR
  isWalking?: string;           // "true" if no reservation
  dispensingTime?: string;      // Time in ms
  isDuplicate?: string;         // "true" for replacement keys
  manualCheckIn?: string;       // "true" if checked in at reception
}

/**
 * Metadata for section events
 */
export interface SectionEventMetadata extends BaseEventMetadata {
  action?: string;              // What's happening in this section
  duration?: string;            // Time spent in section
  isWalking?: string;          // For section R
  roomSelected?: string;        // For section R
  paymentMethod?: string;       // For section P
  amount?: string;             // For section P
  operationId?: string;        // For section P
}

/**
 * Metadata for abandonment events
 */
export interface AbandonmentMetadata extends BaseEventMetadata {
  abandonReason?: string;       // timeout, userExit, etc.
  timeInSection?: string;      // Seconds in section before abandoning
  abandonedSection?: string;   // Human-readable section name
}

/**
 * Main event structure for analytics
 */
export interface JourneyEvent {
  eventCode: JourneyEventCode | string;
  deviceId: string;
  hotelId: string;
  channel: string;
  
  // Reservation identifiers (null for walking guests)
  reservationNumber?: string | null;
  alternateReservationNumbers?: string[];
  
  // Room number (CRITICAL for walking guest correlation)
  roomNumber?: string | null;

  // Guest's full name, top-level like a real kiosk (Insight stores it as «Juan G.»)
  guestName?: string | null;
  
  // Section information
  sectionCode?: SectionCode | string;
  
  // Error information
  errorCode?: string;
  errorDescription?: string;
  
  // Metadata based on event type
  metadata?: BaseEventMetadata | 
            CheckInStartMetadata | 
            CheckInCompleteMetadata | 
            KeyDispenseMetadata | 
            SectionEventMetadata |
            AbandonmentMetadata;
}

/**
 * Journey state for tracking
 */
export interface JourneyState {
  journeyId?: string;
  reservationNumber?: string | null;
  alternateReservationNumbers?: string[];
  roomNumber?: string;
  isWalking: boolean;
  hasPreCheckIn: boolean;
  
  // Timeline
  startTime?: Date;
  lastEventTime?: Date;
  
  // Status tracking
  currentSection?: SectionCode;
  completedSections: SectionCode[];
  
  // Journey phases
  preCheckInStarted: boolean;
  preCheckInCompleted: boolean;
  checkInStarted: boolean;
  checkInCompleted: boolean;
  keyDispensed: boolean;
  
  // Correlation info
  qrUniqueId?: string;
  correlationWindow?: Date; // For 3-hour walking guest window
}

/**
 * Configuration for journey simulation
 */
export interface JourneySimulationConfig {
  // Device configuration
  deviceId: string;
  deviceType: DeviceType;
  hotelId: string;
  apiUrl: string;
  
  // Journey configuration
  guestType: GuestType;
  startFromPreCheckIn: boolean;
  continueExistingPreCheckIn: boolean;
  
  // Timing configuration
  delayBetweenSteps: number;    // ms between sections
  sectionDurations: {
    [key in SectionCode]?: {
      min: number;
      max: number;
    };
  };
  
  // Behavior configuration
  skipSections?: SectionCode[];
  errorRate: number;
  abandonmentRate: number;
  paymentFailureRate: number;
  keyDispenseFailureRate: number;
  
  // Key delivery
  keyDeliveryMethod: KeyDeliveryMethod;
  generateQR: boolean;
  qrType?: QRCodeType;
  
  // Walking guest specific
  walkingGuestRoomSelection: boolean;
}

/**
 * Helper to create proper metadata for walking guests
 */
export function createWalkingGuestMetadata(
  roomNumber: string,
  additionalMetadata?: BaseEventMetadata
): BaseEventMetadata {
  return {
    roomNumber,
    isWalking: "true",
    ...additionalMetadata
  };
}

/**
 * Helper to create correlation-ready event
 */
export function createCorrelatedEvent(
  eventCode: JourneyEventCode,
  deviceId: string,
  hotelId: string,
  journey: JourneyState,
  metadata?: BaseEventMetadata
): JourneyEvent {
  const event: JourneyEvent = {
    eventCode,
    deviceId,
    hotelId,
    channel: getChannelForDevice(deviceId),
  };

  // Add reservation information if available
  if (journey.reservationNumber) {
    event.reservationNumber = journey.reservationNumber;
    if (journey.alternateReservationNumbers?.length) {
      event.alternateReservationNumbers = journey.alternateReservationNumbers;
    }
  } else {
    event.reservationNumber = null; // Explicit null for walking guests
  }

  // Add section if tracking
  if (journey.currentSection) {
    event.sectionCode = journey.currentSection;
  }

  // Ensure roomNumber is in metadata for correlation
  if (journey.roomNumber) {
    event.metadata = {
      roomNumber: journey.roomNumber,
      ...metadata
    };
  } else {
    event.metadata = metadata;
  }

  // Add walking guest flag
  if (journey.isWalking) {
    event.metadata = {
      ...event.metadata,
      isWalking: "true"
    };
  }

  // Add QR unique ID if available
  if (journey.qrUniqueId) {
    event.metadata = {
      ...event.metadata,
      qrUniqueId: journey.qrUniqueId
    };
  }

  return event;
}

/**
 * Get channel based on device type
 */
function getChannelForDevice(deviceId: string): string {
  if (deviceId.includes('web')) return 'WEB';
  if (deviceId.includes('mobile-kiosk')) return 'MOB';
  if (deviceId.includes('mobile-desk')) return 'MRD';
  if (deviceId.includes('KR') || deviceId.includes('kiosk')) return 'CHK';
  if (deviceId.includes('DISP')) return 'DSP';
  return 'UNK';
}

/**
 * Check if journey can be correlated
 */
export function canCorrelateJourney(journey: JourneyState): boolean {
  // Has reservation number - always correlatable
  if (journey.reservationNumber) {
    return true;
  }

  // Walking guest with room number within 3-hour window
  if (journey.isWalking && journey.roomNumber && journey.correlationWindow) {
    const now = new Date();
    const windowEnd = new Date(journey.correlationWindow.getTime() + (3 * 60 * 60 * 1000));
    return now < windowEnd;
  }

  // Has unique QR ID
  if (journey.qrUniqueId) {
    return true;
  }

  return false;
}

/**
 * Validation helpers
 */
export const JourneyValidation = {
  isValidReservationNumber(value: string | null | undefined): boolean {
    if (!value) return false;
    // Must not be empty string
    return value.trim().length > 0;
  },

  isValidRoomNumber(value: string | undefined): boolean {
    if (!value) return false;
    // Basic room number validation
    return /^[A-Z0-9]{1,5}$/.test(value);
  },

  requiresRoomSelection(journey: JourneyState): boolean {
    return journey.isWalking && !journey.roomNumber;
  },

  canStartCheckIn(journey: JourneyState): boolean {
    // Can start if not already started
    return !journey.checkInStarted;
  },

  canCompleteCheckIn(journey: JourneyState): boolean {
    // Must have started and have room number
    return journey.checkInStarted && !journey.checkInCompleted && !!journey.roomNumber;
  },

  canDispenseKey(journey: JourneyState): boolean {
    // Need either completed check-in or pre-check-in with room
    return (journey.checkInCompleted || 
           (journey.preCheckInCompleted && !!journey.roomNumber)) && 
           !journey.keyDispensed;
  }
};

/**
 * Section flow definitions
 */
export const SectionFlow = {
  // Standard flow for reservation guests
  RESERVATION: [SectionCode.S, SectionCode.I, SectionCode.F, SectionCode.U, SectionCode.P],
  
  // Flow for walking guests (includes room selection)
  WALKING: [SectionCode.R, SectionCode.I, SectionCode.F, SectionCode.P],
  
  // Minimal flow for pre-checked guests continuing at kiosk
  PRECHECKED_CONTINUATION: [SectionCode.S],
  
  // Get next section in flow
  getNextSection(current: SectionCode, flow: SectionCode[]): SectionCode | null {
    const index = flow.indexOf(current);
    if (index === -1 || index === flow.length - 1) {
      return null;
    }
    return flow[index + 1];
  }
};